import { types, Instance, getParent } from "mobx-state-tree";
import { SelectModel } from "../../mobxDataModels/selectModel";
import { NumberModel } from "../../mobxDataModels/numberModel";
import { StringModel } from "../../mobxDataModels/stringModel";
import { defaultValues } from "../base";
import { WorkloadDraftMobx } from "./workload.draft";
import { ngParseLink } from "../../utils/linkParser/linkParser";
import { DefaultOptions } from "../../schema/types/workload/workloadOptions";
import jsYaml from "js-yaml";

const metricOptions = [
  { label: "Disabled", value: "disabled" },
  { label: "Concurrent Requests", value: "concurrency" },
  { label: "CPU Utilization", value: "cpu" },
  { label: "KEDA", value: "keda" },
  { label: "Latency", value: "latency" },
  { label: "Memory (%)", value: "memory" },
  { label: "Multi (CPU, Memory, RPS)", value: "multi" },
  { label: "Requests Per Second", value: "rps" },
];

export const WorkloadDraftOptionsModel = types
  .model({
    isLocal: types.boolean,
    isNew: types.boolean,
    _hasAutoscaling: types.optional(types.boolean, true),
    overrideAutoscaling: types.optional(types.boolean, false),
    _location: types.optional(types.string, ""),
    location: types.optional(StringModel, () => StringModel.create({ isRequired: true, label: "Location" })),
    _capacityAI: defaultValues.capacityAI,
    _capacityAIUpdateMinutes: "",
    _debug: defaultValues.debug,
    _suspend: types.optional(types.boolean, false),
    _multiZone: types.optional(types.boolean, false),
    _metric: types.optional(types.string, defaultValues.metric),
    _target: types.optional(types.number, defaultValues.target.default),
    _multiCpuTarget: types.maybe(types.number),
    _multiMemoryTarget: types.maybe(types.number),
    _multiRpsTarget: types.maybe(types.number),
    _metricPercentile: defaultValues.workload.options.metricPercentile,
    _minScale: defaultValues.minScale.default,
    _maxScale: defaultValues.maxScale.default,
    _scaleToZeroDelay: defaultValues.scaleToZeroDelay.default,
    _maxConcurrency: defaultValues.maxConcurrency.default,
    _timeoutSeconds: defaultValues.timeoutSecondsOptions.default,
    _keda: types.optional(types.string, ""),
    capacityAI: defaultValues.capacityAI,
    capacityAIUpdateMinutes: types.optional(StringModel, () =>
      NumberModel.create({
        isRequired: false,
        min: defaultValues.capacityAIUpdateMinutes.min,
        label: "Capacity AI Update Minutes",
      }),
    ),
    debug: defaultValues.debug,
    suspend: types.optional(types.boolean, false),
    multiZone: types.optional(types.boolean, false),
    metric: types.optional(SelectModel, () =>
      SelectModel.create({
        label: "Scaling Strategy",
        initialValue: defaultValues.metric,
        options: metricOptions,
      }),
    ),
    target: types.optional(StringModel, () =>
      NumberModel.create({
        label: "Target",
        isRequired: true,
        initialValue: defaultValues.target.default,
        min: defaultValues.target.min,
        max: defaultValues.target.max,
      }),
    ),
    multiCpuTarget: types.optional(StringModel, () =>
      NumberModel.create({
        label: "CPU Target",
        min: defaultValues.target.min,
        max: 100,
      }),
    ),
    multiMemoryTarget: types.optional(StringModel, () =>
      NumberModel.create({
        label: "Memory Target",
        min: defaultValues.target.min,
        max: 100,
      }),
    ),
    multiRpsTarget: types.optional(StringModel, () =>
      NumberModel.create({
        label: "RPS Target",
        min: defaultValues.target.min,
      }),
    ),
    metricPercentile: types.optional(SelectModel, () =>
      SelectModel.create({
        label: "Metric Percentile",
        options: [
          { label: "p50", value: "p50" },
          { label: "p75", value: "p75" },
          { label: "p99", value: "p99" },
        ],
        initialValue: defaultValues.workload.options.metricPercentile,
      }),
    ),
    minScale: types.optional(StringModel, () =>
      NumberModel.create({
        label: "Min Scale",
        isRequired: true,
        initialValue: defaultValues.minScale.default,
        min: defaultValues.minScale.min,
      }),
    ),
    maxScale: types.optional(StringModel, () =>
      NumberModel.create({
        label: "Max Scale",
        isRequired: true,
        initialValue: defaultValues.maxScale.default,
        min: defaultValues.maxScale.min,
      }),
    ),
    scaleToZeroDelay: types.optional(StringModel, () =>
      NumberModel.create({
        label: "Scale Down Delay (seconds)",
        isRequired: true,
        initialValue: defaultValues.scaleToZeroDelay.default,
        min: defaultValues.scaleToZeroDelay.min,
        max: defaultValues.scaleToZeroDelay.max,
      }),
    ),
    maxConcurrency: types.optional(StringModel, () =>
      NumberModel.create({
        label: "Max Concurrency",
        isRequired: true,
        initialValue: defaultValues.maxConcurrency.default,
        min: defaultValues.maxConcurrency.min,
        max: defaultValues.maxConcurrency.max,
      }),
    ),
    timeoutSeconds: types.optional(StringModel, () =>
      NumberModel.create({
        label: "Timeout Seconds",
        isRequired: true,
        initialValue: defaultValues.timeoutSecondsOptions.default,
        min: defaultValues.timeoutSecondsOptions.min,
      }),
    ),
    // keda: types.optional(WorkloadDraftKedaModel, () => WorkloadDraftKedaModel.create()),
    keda: types.optional(types.string, ""),
  })
  .actions((self) => ({
    reset() {
      self.capacityAI = self._capacityAI;
      const { name: _locationName } = ngParseLink(self._location, { kind: "location" });
      self.overrideAutoscaling = false;
      self.location.setInitialValue(_locationName);
      self.capacityAIUpdateMinutes.setInitialValue(self._capacityAIUpdateMinutes);
      self.debug = self._debug;
      self.target.setInitialValue(String(self._target));
      self.metric.setInitialValue(self._metric);
      self.metricPercentile.setInitialValue(self._metricPercentile);
      self.minScale.setInitialValue(String(self._minScale));
      self.maxScale.setInitialValue(String(self._maxScale));
      self.scaleToZeroDelay.setInitialValue(String(self._scaleToZeroDelay));
      self.maxConcurrency.setInitialValue(String(self._maxConcurrency));
      self.timeoutSeconds.setInitialValue(String(self._timeoutSeconds));
      self.suspend = self._suspend;
      self.multiZone = self._multiZone;
      self.multiCpuTarget.setInitialValue(self._multiCpuTarget ? String(self._multiCpuTarget) : "");
      self.multiMemoryTarget.setInitialValue(self._multiMemoryTarget ? String(self._multiMemoryTarget) : "");
      self.multiRpsTarget.setInitialValue(self._multiRpsTarget ? String(self._multiRpsTarget) : "");
      self.keda = self._keda;
    },
    confirm() {
      if (!self._hasAutoscaling && self.overrideAutoscaling) {
        self._hasAutoscaling = true;
        self.overrideAutoscaling = false;
      }
    },
  }))
  .actions((self) => ({
    afterCreate() {
      self.reset();
    },
    setSuspend(value: boolean) {
      self.suspend = value;
    },
    setMultiZone(value: boolean) {
      self.multiZone = value;
    },
    setOverrideAutoscaling(value: boolean) {
      self.overrideAutoscaling = value;
    },
    setCapacityAI(value: boolean, forDefaultOptions: boolean) {
      self.capacityAI = value;
      if (forDefaultOptions) {
        self._capacityAI = value;
      }
    },
    setDebug(value: boolean) {
      self.debug = value;
    },
    setKeda(value: string) {
      self.keda = value;
    },
    _confirm() {
      self._location = self.location.value;
      self.location.confirm();
      self._capacityAI = self.capacityAI;
      self._debug = self.debug;
      self._metric = self.metric.value as any;
      self.metric.confirm();
      self._capacityAIUpdateMinutes = self.capacityAIUpdateMinutes.value as any;
      self.capacityAIUpdateMinutes.confirm();
      self._target = Number(self.target.value);
      self.target.confirm();
      self._metricPercentile = self.metricPercentile.value;
      self.metricPercentile.confirm();
      self._multiCpuTarget = self.multiCpuTarget.value ? Number(self.multiCpuTarget.value) : undefined;
      self.multiCpuTarget.confirm();
      self._multiMemoryTarget = self.multiMemoryTarget.value ? Number(self.multiMemoryTarget.value) : undefined;
      self.multiMemoryTarget.confirm();
      self._multiRpsTarget = self.multiRpsTarget.value ? Number(self.multiRpsTarget.value) : undefined;
      self.multiRpsTarget.confirm();
      self._minScale = Number(self.minScale.value);
      self.minScale.confirm();
      self._maxScale = Number(self.maxScale.value);
      self.maxScale.confirm();
      self._scaleToZeroDelay = Number(self.scaleToZeroDelay.value);
      self.scaleToZeroDelay.confirm();
      self._maxConcurrency = Number(self.maxConcurrency.value);
      self.maxConcurrency.confirm();
      self._timeoutSeconds = Number(self.timeoutSeconds.value);
      self.timeoutSeconds.confirm();
      self.isNew = false;
      self._suspend = self.suspend;
      self._multiZone = self.multiZone;
      // self.keda.confirm();
      self._keda = self.keda;
    },
  }))
  .actions((self) => ({
    // only called when this one is used as a LocalOptionItem
    defaultOptionsUpdated(defaultOptions: DefaultOptions) {
      // Update values if they were using the same values as default options?
      if (defaultOptions.autoscaling) {
        if (self.metric.value === defaultOptions.autoscaling.metric) {
          self._metric = defaultOptions.autoscaling.metric;
          self.metric.setInitialValue(self._metric);
        }
        if (self.target.value === String(defaultOptions.autoscaling.target)) {
          self._target = defaultOptions.autoscaling.target!;
          self.target.setInitialValue(String(self._target));
        }
        if (self.capacityAIUpdateMinutes.value === String(defaultOptions.capacityAIUpdateMinutes)) {
          self._capacityAIUpdateMinutes = String(defaultOptions.capacityAIUpdateMinutes || "");
          self.capacityAIUpdateMinutes.setInitialValue(self._capacityAIUpdateMinutes);
        }

        const multiCpuItem = defaultOptions.autoscaling.multi?.find((m) => m.metric === "cpu");
        if (multiCpuItem && String(multiCpuItem.target) === self.multiCpuTarget.value) {
          self._multiCpuTarget = multiCpuItem.target;
          self.multiCpuTarget.setInitialValue(String(self._multiCpuTarget));
        } else {
          self._multiCpuTarget = undefined;
          self.multiCpuTarget.setInitialValue("");
        }
        const multiMemoryItem = defaultOptions.autoscaling.multi?.find((m) => m.metric === "memory");
        if (multiMemoryItem && String(multiMemoryItem.target) === self.multiMemoryTarget.value) {
          self._multiMemoryTarget = multiMemoryItem.target;
          self.multiMemoryTarget.setInitialValue(String(self._multiMemoryTarget));
        } else {
          self._multiMemoryTarget = undefined;
          self.multiMemoryTarget.setInitialValue("");
        }
        const multiRpsItem = defaultOptions.autoscaling.multi?.find((m) => m.metric === "rps");
        if (multiRpsItem && String(multiRpsItem.target) === self.multiRpsTarget.value) {
          self._multiRpsTarget = multiRpsItem.target;
          self.multiRpsTarget.setInitialValue(String(self._multiRpsTarget));
        } else {
          self._multiRpsTarget = undefined;
          self.multiRpsTarget.setInitialValue("");
        }

        if (self.metricPercentile.value === String(defaultOptions.autoscaling.metricPercentile)) {
          self._metricPercentile = defaultOptions.autoscaling.metricPercentile!;
          self.metricPercentile.setInitialValue(self._metricPercentile);
        }
        if (self.minScale.value === String(defaultOptions.autoscaling.minScale)) {
          self._minScale = defaultOptions.autoscaling.minScale;
          self.minScale.setInitialValue(String(self._minScale));
        }
        if (self.maxScale.value === String(defaultOptions.autoscaling.maxScale)) {
          self._maxScale = defaultOptions.autoscaling.maxScale;
          self.maxScale.setInitialValue(String(self._maxScale));
        }
        if (self.scaleToZeroDelay.value === String(defaultOptions.autoscaling.scaleToZeroDelay)) {
          self._scaleToZeroDelay = defaultOptions.autoscaling.scaleToZeroDelay;
          self.scaleToZeroDelay.setInitialValue(String(self._scaleToZeroDelay));
        }
        if (self.maxConcurrency.value === String(defaultOptions.autoscaling.maxConcurrency)) {
          self._maxConcurrency = defaultOptions.autoscaling.maxConcurrency;
          self.maxConcurrency.setInitialValue(String(self._maxConcurrency));
        }
      }
      if (self.timeoutSeconds.value === String(defaultOptions.timeoutSeconds)) {
        self._timeoutSeconds = defaultOptions.timeoutSeconds;
        self.timeoutSeconds.setInitialValue(String(self._timeoutSeconds));
      }
      if (self.capacityAI === defaultOptions.capacityAI) {
        self.setCapacityAI(defaultOptions.capacityAI, false);
      }
      if (self.debug === defaultOptions.debug) {
        self._debug = defaultOptions.debug;
        self.debug = self._debug;
      }
      if (self.suspend === defaultOptions.suspend) {
        self._suspend = defaultOptions.suspend;
        self.suspend = self._suspend;
      }
      if (self.multiZone === defaultOptions.multiZone?.enabled) {
        self._multiZone = defaultOptions.multiZone.enabled;
        self.multiZone = self._multiZone;
      }
      // Skip keda
    },
  }))
  .views((self) => ({
    get isDirty() {
      let parent: WorkloadDraftMobx = getParent(self);
      let res = false;
      if (self.isLocal) {
        parent = getParent(self, 2);
        if (self.location.isDirty) res = true;
      }
      if (self.metric.value === "multi") {
      } else {
        if (self.capacityAI !== self._capacityAI) res = true;
      }
      if (self.overrideAutoscaling) res = true;
      if (self.debug !== self._debug) res = true;
      if (self.metric.isDirty) res = true;
      if (self.capacityAIUpdateMinutes.isDirty) res = true;
      if (self.metric.value === "multi") {
        if (self.multiCpuTarget.isDirty) res = true;
        if (self.multiMemoryTarget.isDirty) res = true;
        if (self.multiRpsTarget.isDirty) res = true;
      } else {
        if (self.target.isDirty) res = true;
        if (self.metric.value === "latency") {
          if (self.metricPercentile.isDirty) res = true;
        }
      }
      if (self.minScale.isDirty) res = true;
      if (self.metric.value !== "disabled") {
        if (self.maxScale.isDirty) res = true;
      }
      if (parent.isServerless || parent.isStandard) {
        if (self.scaleToZeroDelay.isDirty) res = true;
      }
      if (parent.isServerless) {
        if (self.maxConcurrency.isDirty) res = true;
      }
      if (self.timeoutSeconds.isDirty) res = true;
      // if (self.keda.isDirty) res = true;
      if (self.keda !== self._keda) res = true;
      if (self.isNew) res = true;
      if (self.suspend !== self._suspend) res = true;
      if (self.multiZone !== self._multiZone) res = true;
      return res;
    },
    get dirtyReason() {
      let parent: WorkloadDraftMobx = getParent(self);
      if (self.isLocal) {
        parent = getParent(self, 2);
        if (self.location.isDirty) return `location | ${self.location.isDirtyReason}`;
      }
      if (self.metric.value === "multi") {
      } else {
        if (self.capacityAI !== self._capacityAI) return `capacity ai`;
      }
      if (self.overrideAutoscaling) return `override autoscaling`;
      if (self.debug !== self._debug) return `debug`;
      if (self.metric.isDirty) return `metric | ${self.metric.isDirtyReason}`;
      if (self.capacityAIUpdateMinutes.isDirty) return `metric | ${self.capacityAIUpdateMinutes.isDirtyReason}`;
      if (self.metric.value === "multi") {
        if (self.multiCpuTarget.isDirty) return `multiCpuTarget | ${self.multiCpuTarget.isDirtyReason}`;
        if (self.multiMemoryTarget.isDirty) return `multiMemoryTarget | ${self.multiMemoryTarget.isDirtyReason}`;
        if (self.multiRpsTarget.isDirty) return `multiRpsTarget | ${self.multiRpsTarget.isDirtyReason}`;
      } else {
        if (self.target.isDirty) return `target | ${self.target.isDirtyReason}`;
        if (self.metric.value === "latency") {
          if (self.metricPercentile.isDirty) return `metricPercentile | ${self.metricPercentile.isDirtyReason}`;
        }
      }
      if (self.minScale.isDirty) return `minScale | ${self.minScale.isDirtyReason}`;
      if (self.metric.value !== "disabled") {
        if (self.maxScale.isDirty) return `maxScale | ${self.maxScale.isDirtyReason}`;
      }
      if (parent.isServerless || parent.isStandard) {
        if (self.scaleToZeroDelay.isDirty) return `scaleToZeroDelay | ${self.scaleToZeroDelay.isDirtyReason}`;
      }
      if (parent.isServerless) {
        if (self.maxConcurrency.isDirty) return `maxConcurrency | ${self.maxConcurrency.isDirtyReason}`;
      }
      if (self.timeoutSeconds.isDirty) return `timeoutSeconds | ${self.timeoutSeconds.isDirtyReason}`;
      // if (self.keda.isDirty) return `keda | ${self.keda.dirtyReason}`;
      if (self.keda !== self._keda) return `keda`;
      if (self.isNew) return `isNew`;
      if (self.suspend !== self._suspend) return `suspend}`;
      if (self.multiZone !== self._multiZone) return `multiZone}`;
      return "";
    },
    get isValid() {
      let parent: WorkloadDraftMobx = getParent(self);
      let res = true;
      if (self.isLocal) {
        parent = getParent(self, 2);
        if (self.location.value.length < 1) res = false;
      }
      const availableMetricOptions = ["disabled", "concurrency", "cpu", "keda", "latency", "memory", "multi", "rps"];
      if (!availableMetricOptions.includes(self.metric.value)) res = false;
      if (self.metric.value === "multi") {
        if (!self.multiCpuTarget.isValid) res = false;
        if (!self.multiMemoryTarget.isValid) res = false;
        if (!self.multiRpsTarget.isValid) res = false;
        if (!self.multiCpuTarget.value && !self.multiMemoryTarget.value && !self.multiRpsTarget.value) res = false;
      } else if (self.metric.value !== "keda") {
        if (!self.target.isValid) res = false;
      }
      if (!self.minScale.isValid) res = false;
      if (self.metric.value !== "disabled") {
        if (!self.maxScale.isValid) res = false;
      }
      if (parent.isServerless || parent.isStandard) {
        if (!self.scaleToZeroDelay.isValid) res = false;
      }
      if (parent.isServerless) {
        if (!self.maxConcurrency.isValid) res = false;
      }
      if (!self.timeoutSeconds.isValid) res = false;
      if (!self.capacityAIUpdateMinutes.isValid) res = false;
      if (parent.isStandard || parent.isStateful) {
        try {
          const kedaObject = jsYaml.load(self.keda);
        } catch (e) {
          res = false;
        }
      }
      return res;
    },
    get invalidReason() {
      let parent: WorkloadDraftMobx = getParent(self);
      if (self.isLocal) {
        parent = getParent(self, 2);
        if (self.location.value.length < 1) return `location is missing for local options`;
      }
      const availableMetricOptions = ["disabled", "concurrency", "cpu", "keda", "latency", "memory", "multi", "rps"];
      if (!availableMetricOptions.includes(self.metric.value))
        return `uses un availableMetricOptions - ${self.metric.value}`;
      if (self.metric.value === "multi") {
        if (!self.multiCpuTarget.isValid) return `multiCpuTarget ${self.multiCpuTarget.invalidReason}`;
        if (!self.multiMemoryTarget.isValid) return `multiMemoryTarget ${self.multiMemoryTarget.invalidReason}`;
        if (!self.multiRpsTarget.isValid) return `multiRpsTarget ${self.multiRpsTarget.invalidReason}`;
        if (!self.multiCpuTarget.value && !self.multiMemoryTarget.value && !self.multiRpsTarget.value)
          return `multi but has no value at all`;
      } else if (self.metric.value !== "keda") {
        if (!self.target.isValid) return `target ${self.target.invalidReason}`;
      }
      if (!self.minScale.isValid) return `minScale ${self.minScale.invalidReason}`;
      if (self.metric.value !== "disabled") {
        if (!self.maxScale.isValid) return `maxScale ${self.maxScale.invalidReason}`;
      }
      if (parent.isServerless || parent.isStandard) {
        if (!self.scaleToZeroDelay.isValid) return `scaleToZeroDelay ${self.scaleToZeroDelay.invalidReason}`;
      }
      if (parent.isServerless) {
        if (!self.maxConcurrency.isValid) return `maxConcurrency ${self.maxConcurrency.invalidReason}`;
      }
      if (!self.timeoutSeconds.isValid) return `timeoutSeconds ${self.timeoutSeconds.invalidReason}`;
      if (!self.capacityAIUpdateMinutes.isValid)
        return `capacityAIUpdateMinutes ${self.capacityAIUpdateMinutes.invalidReason}`;
      if (parent.isStandard || parent.isStateful) {
        try {
          const obj = jsYaml.load(self.keda);
        } catch (e) {
          return `keda | ${e.message}`;
        }
      }
      return "";
    },
    get asObject() {
      let parent: WorkloadDraftMobx = getParent(self);
      if (self.isLocal) {
        parent = getParent(self, 2);
      }
      const res: any = {
        debug: self.debug,
        capacityAI: self.capacityAI,
        timeoutSeconds: Number(self.timeoutSeconds.value),
        suspend: self.suspend,
      };
      if (self.capacityAIUpdateMinutes.value.length > 0) {
        res.capacityAIUpdateMinutes = Number(self.capacityAIUpdateMinutes.value);
      }
      if (self.multiZone) {
        res.multiZone = {
          enabled: self.multiZone,
        };
      }
      if (self._hasAutoscaling || self.overrideAutoscaling) {
        res.autoscaling = {
          minScale: Number(self.minScale.value),
          maxScale: Number(self.maxScale.value),
        };
        if (self.metric.value === "multi") {
          res.autoscaling.multi = [];
          if (self.multiCpuTarget.value && Number(self.multiCpuTarget.value) > 0) {
            res.autoscaling.multi.push({ metric: "cpu", target: Number(self.multiCpuTarget.value) });
          }
          if (self.multiMemoryTarget.value && Number(self.multiMemoryTarget.value) > 0) {
            res.autoscaling.multi.push({ metric: "memory", target: Number(self.multiMemoryTarget.value) });
          }
          if (self.multiRpsTarget.value && Number(self.multiRpsTarget.value) > 0) {
            res.autoscaling.multi.push({ metric: "rps", target: Number(self.multiRpsTarget.value) });
          }
        } else {
          res.autoscaling.metric = self.metric.value;
          if (self.metric.value !== "keda") {
            res.autoscaling.target = Number(self.target.value);
          }
          if (self.metric.value === "latency") {
            res.autoscaling.metricPercentile = self.metricPercentile.value;
          }
          if (self.metric.value === "disabled") {
            res.autoscaling.maxScale = res.autoscaling.minScale;
          }
        }
        if (parent.isServerless) {
          res.autoscaling.maxConcurrency = Number(self.maxConcurrency.value);
        }
        if (parent.isServerless || (parent.isStandard && self.metric.value === "keda")) {
          res.autoscaling.scaleToZeroDelay = Number(self.scaleToZeroDelay.value);
        }
        if (parent.isStandard || parent.isStateful) {
          try {
            const kedaObject = jsYaml.load(self.keda) as any;
            if (self.metric.value === "keda" || kedaObject?.triggers?.length >= 1) {
              res.autoscaling.keda = kedaObject;
            }
          } catch (e) {}
        }
      }
      if (self.isLocal) {
        const { absolute: locationLink } = ngParseLink(self.location.value, { kind: "location" });
        res.location = locationLink;
      }
      return res;
    },
  }));
export interface WorkloadDraftOptionsMobx extends Instance<typeof WorkloadDraftOptionsModel> {}
